import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { useCrud } from '@/hooks/useCrud'
import { camelToWords } from '@/utils/stringHelper'
import CustomerService from './customer.service'
import { createOptions } from '@/utils/arrayHelper'
import { API_RESOURCE } from '@/constants/api.constant'
import useAction from '@/hooks/useAction'

export const useCustomer = () => {
  const customerService = new CustomerService()
  const baseCrud = useCrud(API_RESOURCE.CUSTOMER, customerService)
  const queryClient = useQueryClient()

  const useUploadCustomers = () => {
    return useMutation({
      mutationFn: (file) => customerService.upload(file),
      onSuccess: (res) => {
        toast.info(`Successfully Upload ${camelToWords(API_RESOURCE.CUSTOMER)}`)

        queryClient.invalidateQueries(baseCrud.queryKey.lists())
      },
    })
  }

  const useCreateTemporaryCustomer = () => {
    return useMutation({
      mutationFn: (data) => customerService.createTemporaryCustomer(data),
      onSuccess: (res) => {
        if (res?.success) {
          toast.info(res.message)

          queryClient.invalidateQueries(baseCrud.queryKey.lists())
        }
      },
    })
  }

  const useToggleActiveCustomer = () => {
    return useMutation({
      mutationFn: (id) => customerService.toggleActiveCustomer(id),
      onSuccess: (res) => {
        if (res?.success) {
          toast.info(res.message)

          queryClient.invalidateQueries(baseCrud.queryKey.lists())
        }
      },
    })
  }

  const useCustomerOptions = (parameter = {}) => {
    const { status = 'active', labelKey = 'name', valueKey = '_id', withNilOptions = false, ...rest } = parameter
    return useQuery({
      queryKey: [...baseCrud.queryKey.list({ page: 1, pageSize: 0, filters: { status: status, ...rest } })],
      queryFn: () => customerService.fetchAll({ page: 1, pageSize: 0, filters: { status: status, ...rest } }),
      placeholderData: [],
      select: (data) => createOptions(data?.data, labelKey, valueKey, withNilOptions),
    })
  }

  const useConfirmCustomer = useAction({
    mutationFn: customerService.confirmCustomer,
    queryKey: baseCrud.queryKey,
    message: 'Successfully Confirm Customer',
  })

  return {
    useFetchAllCustomers: baseCrud.useFetchAll,
    useFetchCustomer: baseCrud.useFetchOne,
    useCreateCustomer: baseCrud.useCreate,
    useUpdateCustomer: baseCrud.useUpdate,
    useRemoveCustomer: baseCrud.useRemove,
    useUploadCustomers,
    useConfirmCustomer,
    useCreateTemporaryCustomer,
    useToggleActiveCustomer,
    useCustomerOptions,
  }
}
